import { Result } from '@strength/utils';
import {
  AddSectionGroupedData,
  ExerciseGroupedData,
  SectionGroupedData,
  WorkoutGroupedData,
} from './types';
import {
  HasWorkoutLayer,
  mergeWorkoutLayers,
  Workout,
} from '@strength/domain-workout';
import { WorkoutLayer } from '@strength/db-schema';
import {
  WorkoutLayerCommand,
  WorkoutLayerModelName,
} from './mutation-fns/types';
import {
  WorkoutTemplateData,
  wrapTemplateLayerAsHasWorkoutLayer,
} from '../library/workout-templates/workout-template-utils';
import { pasteCommands } from './copy-paste/copy-paste';
import { exercisesForSection } from './utils';
import { WorkoutWithCycleAndSession } from './types';
import {
  clearWorkoutEdits,
  findWorkoutLayer,
} from './mutation-fns/workout-draft';
import { Metric, WorkoutLayerDomain } from '@strength/domain';

export function selectWorkoutTemplateCommands(
  workoutToPasteInto: Workout,
  workoutLayers: HasWorkoutLayer[],
  workoutTemplate: WorkoutTemplateData,
  modelName: WorkoutLayerModelName,
  setsMetric: Metric | undefined,
): Result<WorkoutLayerCommand[], string> {
  const { template } = workoutTemplate;

  const baseLayer = template[0];
  const restLayers = template.slice(1);
  if (!baseLayer || !restLayers) {
    return {
      ok: false,
      error: 'No template found',
    };
  }

  // section to be pasted into
  const selectionData: WorkoutGroupedData = {
    workoutId: workoutToPasteInto.workoutId,
    data: [
      {
        type: 'AddSection' as const,
        workoutId: workoutToPasteInto.workoutId,
        workout: workoutToPasteInto,
      } as AddSectionGroupedData,
    ],
  };

  // data to be pasted from the workout template
  const workoutToCopy = mergeWorkoutLayers(
    wrapTemplateLayerAsHasWorkoutLayer(baseLayer),
    ...restLayers.map(wrapTemplateLayerAsHasWorkoutLayer),
  );
  const sections = workoutToCopy.sections;
  const sectionData: SectionGroupedData[] = sections.flatMap((section) => {
    const exercises: ExerciseGroupedData[] = exercisesForSection(section).map(
      ({ exercise, superset, alternateGroup }) => {
        return {
          type: 'Exercise' as const,
          workoutId: section.workoutId,
          exercise,
          section,
          superset,
          includesMetrics: exercise.metrics.length > 0,
          sets: exercise.sets,
          disabled: false,
          alternateGroup,
        } as ExerciseGroupedData;
      },
    );

    return {
      type: 'Section',
      section,
      disabled: false,
      exercises,
    };
  });

  const groupedData: WorkoutGroupedData = {
    workoutId: 'foo',
    data: sectionData,
  };

  return pasteCommands(
    modelName,
    workoutLayers,
    groupedData,
    [selectionData],
    setsMetric,
  );
}

export type MultiWorkoutTemplateApplyPlan = {
  commands: WorkoutLayerCommand[];
  // target workout id per template day, in day order
  dayTargets: string[];
  // template has more days than remaining slots from the start
  overflow: boolean;
  // target slots that already have draft (unpublished) content
  draftWorkoutIds: string[];
  // target slots with published Actuals — always excluded from the write
  publishedWorkoutIds: string[];
  // template day numbers (1-based) mapped onto published-Actuals slots
  actualsDayNumbers: number[];
};

export const templateDayIsEmpty = (day: WorkoutLayer): boolean =>
  day.sectionAdds.length === 0 &&
  day.exerciseAdds.length === 0 &&
  day.removals.length === 0;

/**
 * Map a multi-workout template's days (each `template` entry is one day,
 * stored verbatim) onto consecutive target day slots starting at the chosen
 * start slot, following the caller-supplied traversal order (so any groupBy
 * view works). Produces paste commands per target day; a target slot with
 * published Actuals is always excluded from the write; an empty template day
 * no-ops its slot.
 *
 * Callers use `overflow` / `draftWorkoutIds` to gate the commit behind
 * confirmation dialogs before running `commands`.
 */
export function selectMultiWorkoutTemplateCommands({
  orderedWorkouts,
  startWorkoutId,
  template,
  blockWorkoutLayers,
  baseLayers,
  modelName,
  setsMetric,
}: {
  orderedWorkouts: WorkoutWithCycleAndSession[];
  startWorkoutId: string;
  template: WorkoutTemplateData;
  blockWorkoutLayers: HasWorkoutLayer[];
  // the underlying base layers (e.g. block drafts) when clearing an athlete
  // layer — base-layer sections must get removals so they don't show through
  baseLayers?: HasWorkoutLayer[];
  modelName: WorkoutLayerModelName;
  setsMetric: Metric | undefined;
}): Result<MultiWorkoutTemplateApplyPlan, string> {
  const days = template.template;
  if (days.length < 2) {
    return {
      ok: false,
      error: 'Not a multi-workout template',
    };
  }

  const startIndex = orderedWorkouts.findIndex(
    (w) => w.workoutId === startWorkoutId,
  );
  if (startIndex === -1) {
    return {
      ok: false,
      error: 'Start workout not found',
    };
  }

  const targets = orderedWorkouts.slice(startIndex, startIndex + days.length);
  const overflow = targets.length < days.length;

  const draftWorkoutIds: string[] = [];
  const publishedWorkoutIds: string[] = [];
  const actualsDayNumbers: number[] = [];

  const perDay: { workout: WorkoutWithCycleAndSession; day: WorkoutLayer }[] =
    [];
  const dayTargets: string[] = [];
  targets.forEach((workout, i) => {
    const day = days[i];
    if (!day) return;
    dayTargets.push(workout.workoutId);

    if (workout.actual && workout.actual.length > 0) {
      publishedWorkoutIds.push(workout.workoutId);
      // day numbers are 1-based template positions
      actualsDayNumbers.push(i + 1);
      return;
    }

    // an empty template day contributes no commands — its slot stays as-is,
    // so its existing content is never overwritten and must not be flagged
    if (templateDayIsEmpty(day)) return;

    const hasContent = blockWorkoutLayers.some(
      (l) => l.workoutId === workout.workoutId && !templateDayIsEmpty(l.layer),
    );
    if (workout.exists && hasContent) {
      draftWorkoutIds.push(workout.workoutId);
    }

    perDay.push({ workout, day });
  });

  if (perDay.length === 0) {
    return {
      ok: true,
      value: {
        commands: [],
        dayTargets,
        overflow,
        draftWorkoutIds,
        publishedWorkoutIds,
        actualsDayNumbers,
      },
    };
  }

  // every non-empty day pastes into its own single target workout; a day
  // whose commands fail to build aborts the whole apply — a partial
  // progression would silently mislead the coach
  const commands: WorkoutLayerCommand[] = [];
  for (const { workout, day } of perDay) {
    const { workoutLayer, baseLayer } = findWorkoutLayer(
      blockWorkoutLayers,
      workout.workoutId,
      modelName,
      baseLayers,
    );
    // applying a day replaces the target's existing draft content (the
    // draft-overwrite warning gates this) — clear first, then paste on top
    // of the cleared layer. Both must land in ONE command: each command's
    // edits carry a full layer snapshot computed from the original layer, so
    // a separate clear command would be discarded by the paste command's
    // snapshot (last writer wins) and the template would append instead.
    const shouldClear = draftWorkoutIds.includes(workout.workoutId);
    let effectiveLayers = blockWorkoutLayers;
    if (shouldClear && workoutLayer) {
      const cleared = WorkoutLayerDomain.mergeLayerEdits(
        workoutLayer.layer,
        WorkoutLayerDomain.dedupeLayerEdits(
          clearWorkoutEdits(workoutLayer, baseLayer),
        ),
      );
      const clearedLayer: HasWorkoutLayer = { ...workoutLayer, layer: cleared };
      effectiveLayers = blockWorkoutLayers.map((l) =>
        l === workoutLayer ? clearedLayer : l,
      );
    }

    const result = selectWorkoutTemplateCommands(
      workout,
      effectiveLayers,
      { ...template, template: [day] },
      modelName,
      setsMetric,
    );
    if (!result.ok) {
      return { ok: false, error: result.error };
    }
    commands.push(...result.value);
  }

  return {
    ok: true,
    value: {
      commands,
      dayTargets,
      overflow,
      draftWorkoutIds,
      publishedWorkoutIds,
      actualsDayNumbers,
    },
  };
}
